import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
    CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Icon, InputAdornment, Switch,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDSelect from "components/MDSelect";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { coresDoTema } from "assets/coresTema";
import { errorAlert, successAlert } from "utils/Functions";
import { get, postNoResponse } from "utils/WebService";

const NENHUMA = { id: "", value: "", label: "— nenhuma —" };
const formatoNumero = new Intl.NumberFormat("pt-BR");

/**
 * Contas do legado (uma por cobrador do sistema antigo, criadas pela migração) que são a mesma pessoa de uma conta
 * atual. A conta escolhida em "Mesma pessoa que" passa a valer nas análises (Análise de Atendimento): as duas contam
 * como uma só. Os registros continuam com a conta original. Só Administrador e Suporte.
 */
export default function ContasLegadoDialog({ onClose }) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const cores = coresDoTema(darkMode);
    const [carregando, setCarregando] = useState(true);
    const [salvando, setSalvando] = useState(false);
    const [contasLegado, setContasLegado] = useState([]);
    const [contasAtuais, setContasAtuais] = useState([]);
    const [escolhas, setEscolhas] = useState({}); // id da conta do legado -> id da principal ("" = nenhuma)
    const [busca, setBusca] = useState("");
    const [soComAtividade, setSoComAtividade] = useState(true);

    const carregar = () => {
        setCarregando(true);
        get("/users/unificacao")
            .then(res => {
                setContasLegado(res?.contasLegado || []);
                setContasAtuais(res?.contasAtuais || []);
                setEscolhas({});
            })
            .catch(err => errorAlert(err?.message || "Erro ao carregar as contas do legado."))
            .finally(() => setCarregando(false));
    };
    useEffect(carregar, []);

    const opcoes = useMemo(() => [
        NENHUMA,
        ...contasAtuais.map(c => ({ id: c.id, value: c.id, label: c.ativo ? c.nome : `${c.nome} (inativa)` })),
    ], [contasAtuais]);

    const escolhaDe = (conta) => (Object.prototype.hasOwnProperty.call(escolhas, conta.id) ? escolhas[conta.id] : conta.idPrincipal || "");
    const alteradas = contasLegado.filter(c => escolhaDe(c) !== (c.idPrincipal || ""));

    const visiveis = contasLegado.filter(c => {
        const termo = busca.trim().toLowerCase();
        if (termo && !c.nome.toLowerCase().includes(termo) && !(c.nomeSugestao || "").toLowerCase().includes(termo)
            && !(c.nomePrincipal || "").toLowerCase().includes(termo)) return false;
        return !soComAtividade || c.historicos > 0 || c.acordos > 0 || c.idPrincipal;
    });
    const semPrincipalComSugestao = contasLegado.filter(c => !escolhaDe(c) && c.idSugestao);

    const usarSugestoes = () => {
        const novas = { ...escolhas };
        semPrincipalComSugestao.forEach(c => { novas[c.id] = c.idSugestao; });
        setEscolhas(novas);
    };

    const salvar = async () => {
        setSalvando(true);
        let feitas = 0;
        try {
            for (const conta of alteradas) {
                // eslint-disable-next-line no-await-in-loop
                await postNoResponse("/users/unificacao/salvar", { idLegado: conta.id, idPrincipal: escolhaDe(conta) || "" });
                feitas += 1;
            }
            successAlert(`${feitas} conta(s) do legado atualizada(s).`);
            carregar();
        } catch (err) {
            errorAlert(`${feitas} salva(s) antes do erro. ${err?.message || "Erro ao salvar."}`);
            carregar();
        } finally {
            setSalvando(false);
        }
    };

    const celula = {
        display: "table-cell", py: 0.75, px: 1.25, fontSize: "0.8rem", color: `${cores.texto} !important`,
        borderBottom: `1px solid ${cores.divisoria}`, verticalAlign: "middle",
    };
    const cabecalho = { ...celula, fontWeight: "bold", color: `${cores.textoSecundario} !important`, fontSize: "0.75rem" };

    return (
        <Dialog
            open
            onClose={salvando ? undefined : onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{ sx: { background: cores.cartao, color: cores.texto } }}
        >
            <DialogTitle sx={{ pb: 1, color: `${cores.texto} !important` }}>Contas do legado</DialogTitle>
            <DialogContent sx={{ pt: "4px !important" }}>
                <MDTypography variant="caption" color="text" sx={{ display: "block", mb: 1.5 }}>
                    A migração criou uma conta para cada cobrador do sistema antigo (ex.: ELINEIDE.PAIVA). Em &quot;Mesma pessoa que&quot;,
                    escolha a conta atual da mesma pessoa: a Análise de Atendimento passa a contar as duas como uma só (atendimentos,
                    acordos, recebimentos e conversão) e o filtro por carteira mostra a pessoa da equipe. Os registros continuam com a
                    conta original. A sugestão é pelo nome (mesmo primeiro nome e sobrenome); confira antes de salvar.
                </MDTypography>
                <MDBox display="flex" alignItems="center" gap={2} flexWrap="wrap" mb={1.5}>
                    <MDBox sx={{ width: 260 }}>
                        <MDInput
                            size="small"
                            fullWidth
                            placeholder="Buscar conta..."
                            value={busca}
                            onChange={e => setBusca(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Icon fontSize="small" sx={{ color: `${cores.textoSecundario} !important` }}>search</Icon>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </MDBox>
                    <FormControlLabel
                        control={<Switch checked={soComAtividade} onChange={e => setSoComAtividade(e.target.checked)} />}
                        label={<MDTypography variant="caption" color="text">Só contas com atividade</MDTypography>}
                    />
                    <MDButton variant="outlined" color="info" size="small" disabled={!semPrincipalComSugestao.length || salvando} onClick={usarSugestoes}>
                        <Icon sx={{ mr: 0.5 }}>auto_fix_high</Icon> Usar as sugestões ({semPrincipalComSugestao.length})
                    </MDButton>
                </MDBox>
                {carregando ? (
                    <MDBox display="flex" justifyContent="center" py={4}><CircularProgress size={28} color="info" /></MDBox>
                ) : (
                    <TableContainer sx={{ boxShadow: "none", background: "transparent", borderRadius: 0, maxHeight: 460 }}>
                        <Table size="small" stickyHeader sx={{ display: "table" }}>
                            <TableHead sx={{ display: "table-header-group" }}>
                                <TableRow sx={{ display: "table-row" }}>
                                    {["Conta do legado", "Atividade", "Mesma pessoa que", "Sugestão"].map(t => (
                                        <TableCell key={t} sx={{ ...cabecalho, background: `${cores.painel} !important` }}>{t}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody sx={{ display: "table-row-group" }}>
                                {visiveis.map(c => {
                                    const valor = escolhaDe(c);
                                    const mudou = valor !== (c.idPrincipal || "");
                                    return (
                                        <TableRow key={c.id} sx={{ display: "table-row", background: mudou ? cores.hover : "transparent" }}>
                                            <TableCell sx={{ ...celula, fontWeight: "bold" }}>
                                                {c.nome}{!c.ativo && <MDBox component="span" sx={{ color: "inherit", fontWeight: 400, opacity: 0.8 }}> (inativa)</MDBox>}
                                            </TableCell>
                                            <TableCell sx={celula}>
                                                {formatoNumero.format(c.historicos)} históricos · {formatoNumero.format(c.acordos)} acordos
                                            </TableCell>
                                            <TableCell sx={{ ...celula, minWidth: 280 }}>
                                                <MDSelect
                                                    isMulti={false}
                                                    name={`principal-${c.id}`}
                                                    value={opcoes.find(o => o.value === valor) || NENHUMA}
                                                    options={opcoes}
                                                    onChange={v => setEscolhas(atual => ({ ...atual, [c.id]: v?.value || "" }))}
                                                />
                                            </TableCell>
                                            <TableCell sx={celula}>
                                                {c.idSugestao ? (
                                                    <MDBox display="flex" alignItems="center" gap={1} sx={{ color: "inherit" }}>
                                                        {c.nomeSugestao}
                                                        {valor !== c.idSugestao && (
                                                            <Tooltip title="Usar a sugestão">
                                                                <MDButton size="small" variant="text" color="info" sx={{ minWidth: 0, px: 1 }}
                                                                    onClick={() => setEscolhas(atual => ({ ...atual, [c.id]: c.idSugestao }))}>
                                                                    usar
                                                                </MDButton>
                                                            </Tooltip>
                                                        )}
                                                    </MDBox>
                                                ) : "–"}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                                {visiveis.length === 0 && (
                                    <TableRow sx={{ display: "table-row" }}>
                                        <TableCell colSpan={4} align="center" sx={celula}>Nenhuma conta do legado.</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <MDButton variant="outlined" color="info" onClick={onClose} disabled={salvando}>Fechar</MDButton>
                <MDButton variant="contained" color="info" onClick={salvar} disabled={!alteradas.length || salvando}>
                    {salvando ? <CircularProgress size={18} color="inherit" /> : `Salvar alterações (${alteradas.length})`}
                </MDButton>
            </DialogActions>
        </Dialog>
    );
}

ContasLegadoDialog.propTypes = {
    onClose: PropTypes.func.isRequired,
};
